import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { isSubscriptionActive, getTrialDaysRemaining, PRICING } from '../../utils/subscriptionUtils';
import {
  CreditCard, Calendar, Clock, AlertTriangle, CheckCircle,
  ExternalLink, Loader, Shield,
} from 'lucide-react';

export default function SubscriptionManagement({ user, companyId }) {
  const { t } = useTranslation();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    const unsub = onSnapshot(doc(db, 'companies', companyId), (snap) => {
      if (snap.exists()) setCompany(snap.data());
      setLoading(false);
    }, (error) => {
      console.error('Error listening to company:', error);
      setLoading(false);
    });
    return () => unsub();
  }, [companyId]);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const createPortalSession = httpsCallable(functions, 'shifts_createPortalSession');
      const result = await createPortalSession({
        companyId,
        returnUrl: window.location.href,
      });
      if (result.data?.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      console.error('Portal error:', err);
    }
    setPortalLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const status = company?.shiftsSubscriptionStatus || 'none';
  const active = isSubscriptionActive(status);
  const trialDays = getTrialDaysRemaining(company?.shiftsTrialEndsAt);
  const isTrial = status === 'trialing';
  const bmcStatus = company?.subscriptionStatus;
  const hasBmcBundle = ['active', 'trial'].includes(bmcStatus);
  const price = hasBmcBundle ? PRICING.BUNDLE : PRICING.STANDALONE;

  const statusConfig = {
    trialing: { color: 'text-blue-600 bg-blue-50', icon: Clock, label: t('subscription.status.trialing', 'Trial') },
    active: { color: 'text-green-600 bg-green-50', icon: CheckCircle, label: t('subscription.status.active', 'Active') },
    past_due: { color: 'text-amber-600 bg-amber-50', icon: AlertTriangle, label: t('subscription.status.past_due', 'Past Due') },
    canceling: { color: 'text-amber-600 bg-amber-50', icon: Clock, label: t('subscription.status.canceling', 'Canceling') },
    cancelled: { color: 'text-red-600 bg-red-50', icon: AlertTriangle, label: t('subscription.status.cancelled', 'Cancelled') },
    locked: { color: 'text-red-600 bg-red-50', icon: AlertTriangle, label: t('subscription.status.locked', 'Locked') },
    none: { color: 'text-gray-600 bg-gray-50', icon: Calendar, label: t('subscription.status.none', 'No Subscription') },
  };

  const cfg = statusConfig[status] || statusConfig.none;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
        <CreditCard size={24} /> {t('subscription.manage')}
      </h1>
      <p className="text-gray-400 mb-8">{t('subscription.manageSubtitle', 'Manage your BuildMyShifts subscription')}</p>

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">{t('subscription.statusTitle', 'Subscription Status')}</h3>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cfg.color}`}>
            <cfg.icon size={14} /> {cfg.label}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-sm text-gray-400">{t('subscription.planLabel', 'Plan')}</p>
            <p className="font-semibold text-white">BuildMyShifts</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-sm text-gray-400">{t('subscription.priceLabel', 'Price')}</p>
            <p className="font-semibold text-white">
              £{price}/user/month
              {price === PRICING.BUNDLE && (
                <span className="text-xs text-green-600 ml-1">({t('subscription.bundleInline', 'bundle')})</span>
              )}
            </p>
          </div>
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-sm text-gray-400">
              {isTrial ? t('subscription.trialEndsIn', 'Trial Ends In') : t('subscription.renewalLabel', 'Renewal')}
            </p>
            <p className="font-semibold text-white">
              {isTrial
                ? `${trialDays} ${t('subscription.daysLabel', 'days')}`
                : active
                  ? t('subscription.autoRenew', 'Auto-renew')
                  : '—'}
            </p>
          </div>
        </div>
      </div>

      {isTrial && trialDays <= 3 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-5 mb-6 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">{t('subscription.trialEndingSoon', 'Trial ending soon')}</p>
            <p className="text-sm text-amber-700 mt-1">
              {t('subscription.trialEndingDetailed', 'Your trial ends in {{days}} day{{suffix}}. Subscribe now to keep using BuildMyShifts.', {
                days: trialDays,
                suffix: trialDays !== 1 ? 's' : '',
              })}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="font-semibold text-white mb-4">{t('subscription.billing', 'Billing')}</h3>

        {active && status !== 'trialing' ? (
          <button
            onClick={handleManageBilling}
            disabled={portalLoading}
            className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {portalLoading ? <Loader size={16} className="animate-spin" /> : <ExternalLink size={16} />}
            {t('subscription.manageBillingStripe', 'Manage Billing on Stripe')}
          </button>
        ) : (
          <a
            href="/checkout"
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            <CreditCard size={16} />
            {isTrial ? t('subscription.subscribeCta', 'Subscribe Now') : t('subscription.reactivate', 'Reactivate Subscription')}
          </a>
        )}

        <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
          <Shield size={12} /> {t('subscription.securePayments', 'Payments processed securely by Stripe')}
        </div>
      </div>
    </div>
  );
}
